import { convertCurrency, convertToUSD, formatDate } from 'src/utils/functions';
import { EditMode, FinancialKPIConfig, MetricsState, UserReportAccessTypes } from './propTypes';
import { inputSections } from './configs/FinancialInputConfig';
import { monthNames } from 'src/const';

export const convertData = (input, localCurrency: null | string = null) => {
  const convertCurrencyWrapper = (value: number) => {
    const convertedCurrency =
      localCurrency && localCurrency !== 'USD' && !isNaN(value)
        ? convertToUSD(localCurrency, value)
        : value;
    return convertedCurrency;
  };

  // Convert booked_daily_rate_ltm_monthly to adr
  const adr = Object.entries(input.booked_daily_rate_ltm_monthly).map(([date, value]) => ({
    date,
    adr:
      value !== null && value !== undefined ? convertCurrencyWrapper(value as number) : 'missing',
  }));

  // Convert occupancy_rate_ltm_monthly to occupancy
  const occupancy = Object.entries(input.occupancy_rate_ltm_monthly).map(([date, value]) => ({
    date,
    occupancy: value !== null && value !== undefined ? value : 'missing',
  }));

  // Convert revenue_ltm_monthly to revenue
  const revenue = Object.entries(input.revenue_ltm_monthly).map(([date, value]) => ({
    date,
    revenue:
      value !== null && value !== undefined ? convertCurrencyWrapper(value as number) : 'missing',
  }));

  // Convert no_of_bookings_ltm_monthly to bookings
  const bookings = Object.entries(input.no_of_bookings_ltm_monthly).map(([date, value]) => ({
    date,
    booking: value !== null && value !== undefined ? value : 'missing',
  }));

  // Prepare the final output object
  const output = {
    // markerType: 'full',
    listingID: input.listingID,
    name: input.name,
    thumbnail_url: input.thumbnail_url,
    latitude: input.latitude,
    longitude: input.longitude,
    room_type: input.room_type,
    property_type: '-1',
    bedrooms: input.bedrooms,
    bathrooms: input.bathrooms,
    accommodates: input.accommodates,
    host_name: input.host_name,
    minimum_nights: input.minimum_nights,
    cleaningFee: convertCurrencyWrapper(input.cleaning_fee),
    extraGuestFee: 0, // Assuming 0 as extra guest fee is not provided
    hostId: input.host_id,
    annualRevenueLTM: convertCurrencyWrapper(input.annual_revenue_ltm),
    host_listing_count: null, // Assuming null as host listing count is not provided
    review_scores_rating: input.reveiw_scores_rating,
    amenities: input.amenities,
    visible_review_count: input.visible_review_count,
    adr: adr,
    occupancy: occupancy,
    revenue: revenue,
    bookings: bookings,
    activeDaysCount: input.active_days_count_ltm,
    last_seen: null, // TODO: Add last seen date
    confidenceScoreRelatedData: {
      dataQualityScore: 91, // Assuming fixed data quality score as it's not in input
      distance: 1606, // Assuming a fixed distance as it's not in input
      isNumberOfBathroomDifference: false, // Assuming false as it's not in input
      isHouseTypeDifference: false, // Assuming false as it's not in input
      confidenceScore: 95.5, // Assuming a fixed confidence score as it's not in input
    },
    revenuePotential: convertCurrencyWrapper(input.revenue_potential),
    occupancyRateLTM: input.avg_occupancy_rate_ltm,
    adrLTM: convertCurrencyWrapper(input.avg_booked_daily_rate_ltm),
    no_of_bookings_ltm: input.no_of_bookings_ltm,
    is_premium: true, // Assuming true as is_premium is not provided
    rest_thumbnails: input.thumbnail_url_extended,
    similarity_score: input.similarity_score,
    similarity_score_meta: input.similarity_score_meta,
    rank: input.rank,
  };

  return output;
};

export const checkIfUserAllowedToEditReport = (userEmail: string, reportEmail: string | null) => {
  if (userEmail === 'Trial') {
    return false;
  }

  if (!reportEmail) {
    return false;
  }

  if (userEmail === reportEmail) {
    return true;
  }

  return false;
};

export const convertApiCurrency = (
  apiOutput: any,
  currency: string,
  convertBackToUSD: boolean = false,
  isSendingBackToApi: boolean = false
) => {
  const key_value_type_map = {
    property_price: 'int',
    monthly_rent: 'int',
    ltm_revenue: 'int',
    ltm_nightly_rate: 'int',
    monthly_revenue: 'int',
    monthly_adr: 'int',
    yearly_net_profit_before_tax: 'int',
    monthly_profit: 'int',
    monthly_expense: 'int',
    one_time_cost: 'int',
    is_loan_taken: 'int',
    loan_tenure: 'int',
    down_payment: 'int',
    hoa_fee_annual: 'int',
    maintenance_annual: 'int',
    insurance_annual: 'int',
    electricity_annual: 'int',
    wifi_annual: 'int',
    gas_annual: 'int',
    others_annual: 'int',
    furnishing_cost: 'int',
    closing_cost: 'int',
    property_appreciation_rate: 'int',
    owner_occupied_days: 'int',
    // Adding new currency-related fields
    property_tax: 'int',
    mortgage_payment: 'int',
    property_management_fee: 'int',
    service_fees: 'int',
    // Additional fields from report
    property_value: 'int',
    cleaning_fee: 'int',
    extra_guest_fee: 'int',
    annual_revenue_ltm: 'int',
    revenue_potential: 'int',
    avg_booked_daily_rate_ltm: 'int',
    total_expenses: 'int',
    net_operating_income: 'int',
    five_years_cash_flow: 'int',
    total_cash_invested: 'int',
    total_profit_when_sold: 'int',
    management_fee: 'int',
    startup_costs_total: 'int',
    remodelling: 'int',
    mortgage_annual: 'int',
    income_tax: 'int',
    depreciation: 'int',
    management_fee_earned: 'int',
    taxes_total: 'int',
    cost_to_sell: 'int',
    vacancy_cost: 'int',
    lodging_tax: 'int',
    tech: 'int',
    marketing: 'int',
    total_revenue: 'int',
    total_net_operating_income: 'int',
  };

  const currencyKeys = [
    'hoa_fee_annual',
    'maintenance_annual',
    'insurance_annual',
    'electricity_annual',
    'wifi_annual',
    'gas_annual',
    'others_annual',
    'annual_operating_expenses_total',
    'furnishing_cost',
    'closing_cost',
    'startup_costs_total',
    'property_price',
    'monthly_rent',
    'ltm_revenue',
    'ltm_nightly_rate',
    'monthly_revenue',
    'monthly_adr',
    'yearly_net_profit_before_tax',
    'monthly_profit',
    'yearly_expense',
    'cleaning_fee',
    'annual_revenue_ltm',
    'revenue_potential',
    'avg_booked_daily_rate_ltm',
    'booked_daily_rate_ltm_monthly',
    'revenue_ltm_monthly',
    'monthly_expense',
    'one_time_cost',
    'down_payment',
    'property_tax',
    'mortgage_payment',
    'property_management_fee',
    'service_fees',
    'property_value',
    'total_expenses',
    'ltm_revenue',
    'ltm_nightly_rate',
    'net_operating_income',
    'yearly_net_profit_before_tax',
    'five_years_cash_flow',
    'total_cash_invested',
    'total_profit_when_sold',
    'management_fee',
    'remodelling',
    'mortgage_annual',
    'income_tax',
    'depreciation',
    'management_fee_earned',
    'taxes_total',
    'cost_to_sell',
    'vacancy_cost',
    'lodging_tax',
    'tech',
    'marketing',
    'total_revenue',
    'total_net_operating_income',
  ];

  const convertCurrencyWrapper = (currency: string, value: number, key: string) => {
    let convertedCurrency: number | null = null;
    if (convertBackToUSD) {
      convertedCurrency = convertToUSD(currency, value);
    } else {
      convertedCurrency = convertCurrency(currency, value);
    }

    if (isSendingBackToApi && key_value_type_map[key] === 'int') {
      convertedCurrency = Math.round(convertedCurrency);
    }

    return convertedCurrency;
  };

  const convertValues = (obj: any) => {
    for (let key in obj) {
      if (currencyKeys.includes(key)) {
        if (typeof obj[key] === 'number') {
          // Convert simple numeric values
          obj[key] = convertCurrencyWrapper(currency, obj[key], key);
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          // Check if the object contains date keys with numeric values
          // Handles monthly data : e.g: annual_revenue_monthly: {date: value}[]
          for (let dateKey in obj[key]) {
            if (typeof obj[key][dateKey] === 'number') {
              obj[key][dateKey] = convertCurrencyWrapper(currency, obj[key][dateKey], key);
            }
          }
        }
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Recursively handle nested objects
        convertValues(obj[key]);
      }
    }
  };

  convertValues(apiOutput);

  return apiOutput;
};

export const getIfReportTypeDisabled = (
  reportType: UserReportAccessTypes,
  uiElement: string,
  elementIndex: number = 0
): boolean => {
  switch (uiElement) {
    case 'RevenueLTMGraph':
      if (['starter'].includes(reportType)) return true;
      break;
    case 'CompsFilterListing':
      if (['starter'].includes(reportType) && elementIndex > 2) return true;
      break;
    case 'CompsLtmRevenueBars':
      if (['starter', 'pro'].includes(reportType)) return true;
      break;
    case 'CompsFilterFetchMoreListingsButton':
      if (['starter'].includes(reportType)) return true;
      break;
    case 'CompsFilterListingVisibilityToggleSwitch':
      if (['starter'].includes(reportType)) return true;
      break;
    case 'ListingMarker':
      if (['starter'].includes(reportType) && elementIndex > 2) return true;
      break;
    case 'ListingMarkerCardLTMHistoryVisibility':
      if (['starter', 'pro'].includes(reportType)) return true;
      break;
  }

  return false;
};

export const feetToMeterConversion = (value: number) => {
  const conversionFactor = 3.28084;
  const convertedValue = value / conversionFactor;
  return convertedValue;
};

export const meterToFeetConversion = (value: number) => {
  const conversionFactor = 3.28084;
  const convertedValue = value * conversionFactor;
  return convertedValue;
};

export const initializeMetricsState = (
  config: Record<string, FinancialKPIConfig>,
  reportType: EditMode
) => {
  const state: MetricsState = {};
  const presetVisibilityString = localStorage.getItem('kpi_visibility') || '';
  const presetVisibility = presetVisibilityString ? JSON.parse(presetVisibilityString) : {};

  for (const key in config) {
    const metricConfig = config[key];
    const isAllowed = metricConfig.allowedModes.includes(reportType);
    const isDefaultVisible = metricConfig.showAsDeafaultIn.includes(reportType);

    state[key] = {
      visibility: presetVisibility?.[key] && isAllowed ? true : isDefaultVisible,
      config: metricConfig,
      allowed: isAllowed,
    };
  }

  return state;
};

export const initializeInputEditState = kpiData => {
  const initialState: Record<string, any> = {};

  Object.values(inputSections).forEach(sectionConfig => {
    if (sectionConfig.canToggle && sectionConfig.toggleKey) {
      initialState[sectionConfig.toggleKey] =
        kpiData?.[sectionConfig.toggleKey] == 1 ? true : false;
    }

    Object.keys(sectionConfig.fields).forEach(fieldKey => {
      initialState[fieldKey] =
        kpiData?.[fieldKey] ||
        (sectionConfig.fields[fieldKey].isDropdown
          ? sectionConfig?.fields?.[fieldKey]?.dropdownOptions?.[0] || 0
          : 0);
    });
  });

  return initialState;
};

export const formatDateYearmonthName = (date: string) => {
  const year = date.split('-')[0];
  const month = date.split('-')[1];
  const monthName = Number(month) ? monthNames[Number(month) - 1] : '';
  return `${year} ${monthName}`;
};

export const getLtmDates = (kpiData: any) => {
  try {
    let months =
      kpiData && kpiData[50] && kpiData[50].monthly_adr ? Object.keys(kpiData[50].monthly_adr) : [];
    if (months.length === 0) {
      return '';
    }
    // months: 2024-01, 2024-02, .... month string
    // Sort the months by dates
    months.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA.getTime() - dateB.getTime();
    });
    const lastMonth = months[months.length - 1];
    const monthStartingOfTheYear = months[months.length - 12];

    const formattedLastMonth = formatDateYearmonthName(lastMonth);
    const formattedMonthStartingOfTheYear = formatDateYearmonthName(monthStartingOfTheYear);

    return `${formattedMonthStartingOfTheYear} - ${formattedLastMonth}`;
  } catch (error) {
    return '';
  }
};
